import { getModuleVersionInstances, isArtifactFeedServiceConfigured } from '@modules/artifactfeeds'

import type { Gateway } from '../gateways.interface'

import { mapModuleVersionInstanceToHostedModule } from './mapModuleVersionInstanceToHostedModule'

function gatewayNeedsModuleHydration(gateway: Gateway): boolean {
  const hasIds = (gateway.moduleVersionInstanceIds?.length ?? 0) > 0
  const hasModules = (gateway.modules?.length ?? 0) > 0
  return hasIds && !hasModules
}

export async function hydrateGatewaysHostedModules(projectId: string, gateways: Gateway[]): Promise<Gateway[]> {
  if (!projectId || gateways.length === 0 || !isArtifactFeedServiceConfigured()) {
    return gateways
  }

  if (!gateways.some(gatewayNeedsModuleHydration)) {
    return gateways
  }

  const moduleVersionInstanceIds = [
    ...new Set(
      gateways
        .flatMap((gateway) => gateway.moduleVersionInstanceIds ?? [])
        .map((id) => id.trim())
        .filter((id) => id.length > 0)
    ),
  ]

  if (moduleVersionInstanceIds.length === 0) {
    return gateways
  }

  const instances = await getModuleVersionInstances(projectId, moduleVersionInstanceIds)
  if (instances.length === 0) {
    return gateways
  }

  const instanceById = new Map(instances.map((instance) => [instance.moduleVersionInstanceId, instance]))

  return gateways.map((gateway) => {
    if (!gatewayNeedsModuleHydration(gateway)) {
      return gateway
    }

    const modules = (gateway.moduleVersionInstanceIds ?? [])
      .map((id) => instanceById.get(id.trim()))
      .filter((instance): instance is NonNullable<typeof instance> => instance != null)
      .map(mapModuleVersionInstanceToHostedModule)

    if (modules.length === 0) {
      return gateway
    }

    return {
      ...gateway,
      modules,
      modulesCount: modules.length,
    }
  })
}
